import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { HdGateInfoService } from '@/app/human-design/chart/bodygraph/hd-gate-info.service';
import {
  HD_MEMORY_CHEAT_SHEET_COLOR,
  HD_MEMORY_CIRCUITRY_ASSET,
} from '@/app/human-design/chart/memory/hd-memory.metadata';
import { MemoryCheatBlockVm } from './hd-memory-cheat-block.component';

/**
 * Poortvolgorde per zijde, 1:1 uit het Figma-design (node 1410-1596). Elke
 * zijde is een 2-koloms rooster (rij-voor-rij), links van de centrale
 * bodygraph de zes poorten van de linkerhelft, rechts de zes van de
 * rechterhelft.
 */
const HD_MEMORY_LEFT_GATES: readonly number[] = [9, 52, 54, 32, 44, 26];
const HD_MEMORY_RIGHT_GATES: readonly number[] = [11, 56, 33, 13, 15, 5];

/**
 * Pagina met de Geheugencircuit cheat sheet, nagebouwd uit het Figma-design
 * "A3 Memory Circuit" (node 1410-1596, zonder branding-footer/WIP-vermelding).
 * Vier kolommen poort-blokjes (twee links, twee rechts) rond de centrale
 * bodygraph-afbeelding, met onderaan de twee algemene info-blokken over het
 * geheugencircuit.
 *
 * Per-poort memory-keynote + -omschrijving komen uit de `human-design/memory`-
 * scope; de I Ching-omschrijving en de twee algemene keynotes uit de bestaande
 * `human-design/gates`-scope (via {@link HdGateInfoService.gateInfoIn}).
 * Taalwissel via de gedeelde content-taal-picker; nog niet vertaalde EN-velden
 * vallen terug op NL.
 */
@Component({
  selector: 'hd-memory-cheat-sheet-page',
  standalone: false,
  templateUrl: './hd-memory-cheat-sheet-page.component.html',
  styleUrl: './hd-memory-cheat-sheet-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdMemoryCheatSheetPageComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly pageTitle = computed(() => this.text('pageTitle'));
  protected readonly pageSubtitle = computed(() => this.text('pageSubtitle'));
  protected readonly circuitImage = HD_MEMORY_CIRCUITRY_ASSET;
  protected readonly chartAlt = computed(() => this.text('chartAlt'));

  protected readonly circuitTitle = computed(() => this.text('circuitInfo.title'));
  protected readonly leftBullets = computed(() => this.list('circuitInfo.left'));
  protected readonly rightBullets = computed(() => this.list('circuitInfo.right'));

  protected readonly leftBlocks = computed(() => HD_MEMORY_LEFT_GATES.map(gate => this.block(gate)));
  protected readonly rightBlocks = computed(() => HD_MEMORY_RIGHT_GATES.map(gate => this.block(gate)));

  constructor() {
    // Beide content-talen eager laden zodat de taalwissel én de NL-fallback
    // meteen correcte tekst opleveren.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  protected print(): void {
    window.print();
  }

  private block(gate: number): MemoryCheatBlockVm {
    const translations = this.gateInfo.gateInfoIn(gate, this.lang()).translations;
    const keynotes = [translations.keynote1, translations.keynote2]
      .filter(Boolean)
      .map(keynote => keynote.toLowerCase())
      .join(', ');
    return {
      gate,
      color: HD_MEMORY_CHEAT_SHEET_COLOR[gate] ?? '#f1f1f1',
      keynote: this.text(`gates.${gate}.keynote`),
      description: this.text(`gates.${gate}.description`),
      iching: translations.shortIchingDescription,
      gateOf: keynotes ? `${this.text('gateOf')} ${keynotes}` : '',
    };
  }

  /** Scope-tekst in de content-taal, met NL-fallback voor (nog) lege EN-velden. */
  private text(key: string): string {
    const lang = this.lang();
    const value = this.gateInfo.cheatSheetTextIn('memory', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('memory', key, 'nl');
  }

  private list(key: string): readonly string[] {
    const lang = this.lang();
    const value = this.gateInfo.cheatSheetListIn('memory', key, lang);
    if (value.length > 0 || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetListIn('memory', key, 'nl');
  }
}
